import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Youtube, Sparkles, Globe, BadgeCheck, ExternalLink, Layers } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import toolsData from "@/data/publicTools.json";

export default function PublicTool() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tool = toolsData.tools.find((t) => t.slug === slug);
  const catLabel = tool && (toolsData.categories.find((c) => c.key === tool.category)?.label || tool.category);

  useEffect(() => {
    if (tool) document.title = `Learn ${tool.name} — Free Structured Course | AI Pathshaalaa`;
    window.scrollTo(0, 0);
  }, [tool]);

  if (!tool) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-24 text-center">
          <h1 className="font-heading font-bold text-3xl">Tool not found</h1>
          <Link to="/ai-tools" className="mt-4 inline-block text-primary font-semibold hover:underline">Browse all AI tools →</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const lessons = toolsData.lesson_steps.map((s) => ({
    label: s.label,
    title: s.title.replace(/\{name\}/g, tool.name),
    desc: s.desc.replace(/\{name\}/g, tool.name),
  }));
  const related = toolsData.tools.filter((t) => t.category === tool.category && t.slug !== slug).slice(0, 5);

  const start = () => navigate(user ? `/tools/${tool.slug}` : "/quiz");

  return (
    <div className="min-h-screen bg-background" data-testid="public-tool-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <nav className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <Link to="/ai-tools" className="hover:text-primary transition-colors">AI tools</Link>
            <span className="mx-2">/</span>
            <span className="text-primary">{tool.name}</span>
          </nav>
          <h1 className="mt-4 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl">Learn {tool.name}</h1>
          <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">{tool.tagline}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: Layers, label: catLabel },
              { icon: BadgeCheck, label: "11-lesson complete course" },
              { icon: Globe, label: "English · Hindi · Hinglish" },
              { icon: Youtube, label: "100% free videos" },
            ].map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card text-muted-foreground">
                <b.icon className="h-3.5 w-3.5 text-primary" /> {b.label}
              </span>
            ))}
            <a href={tool.official_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card text-muted-foreground hover:text-primary transition-colors">
              <ExternalLink className="h-3.5 w-3.5 text-primary" /> Official site
            </a>
          </div>
        </motion.div>

        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-10">
          <h2 className="font-heading font-bold text-xl md:text-2xl">The full {tool.name} curriculum</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            From what {tool.name} is and how pricing works, all the way to real-world workflows and building a real
            project — each lesson taught by the clearest free YouTube teacher we can find.
          </p>
          <ol className="mt-6 space-y-3" data-testid="public-tool-lessons">
            {lessons.map((m, i) => (
              <li key={m.title} className="flex items-start gap-4 rounded-2xl border border-border bg-card px-5 py-4">
                <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary font-heading font-bold text-sm shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0">
                  <span className="font-semibold block">{m.title}</span>
                  <span className="text-sm text-muted-foreground">{m.desc}</span>
                </span>
                <Check className="h-4 w-4 text-emerald-600 ml-auto shrink-0 mt-1" />
              </li>
            ))}
          </ol>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
          className="mt-10 rounded-3xl border border-primary/25 bg-card p-7 md:p-8 relative overflow-hidden beam-top">
          <span className="orb h-56 w-56 bg-primary/40 -top-16 -right-10" />
          <h2 className="relative font-heading font-bold text-2xl">Videos stay free.<br />We put them in order.</h2>
          <p className="relative mt-2 text-sm text-muted-foreground max-w-xl">
            AI Pathshaalaa curates the best free YouTube tutorials for {tool.name} into one structured course — with AI
            summaries, quizzes, notes and progress tracking, refreshed daily as new videos are published.
          </p>
          <div className="relative mt-6 flex items-center gap-4 flex-wrap">
            <Button size="lg" data-testid="public-tool-cta" onClick={start} className="rounded-full px-7">
              Start the {tool.name} course <ArrowRight className="h-4 w-4" />
            </Button>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Youtube className="h-4 w-4 text-red-500" /> Videos play in the official YouTube player
            </span>
          </div>
        </motion.section>

        {related.length > 0 && (
          <div className="mt-10">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">More {catLabel} tools</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {related.map((t) => (
                <Link key={t.slug} to={`/ai-tools/${t.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                  <Sparkles className="h-3.5 w-3.5 text-primary" /> {t.name}
                </Link>
              ))}
              <Link to="/ai-tools"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                All AI tools <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
